// =====================================================================
// Quadros (Kanban integrado / Q1 / S59) — página /quadros.
// Sem boardId na URL → lista de quadros. Com boardId → BoardView.
// NÃO confundir com pages/Kanban (Kanban de TICKETS, intocado).
// =====================================================================
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper, Button, Typography, Grid, CircularProgress, IconButton, Tooltip
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import ArchiveIcon from "@material-ui/icons/Archive";
import UnarchiveIcon from "@material-ui/icons/Unarchive";
import DeleteIcon from "@material-ui/icons/Delete";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import api from "../../services/api";
import { listBoards, archiveBoard, restoreBoard, removeBoard, boardErrorMessage, boardBgStyle } from "../../services/boardService";
import BoardFormDialog from "./BoardFormDialog";
import BoardView from "./BoardView";
import NotificationsBell from "./NotificationsBell";

const useStyles = makeStyles((theme) => ({
  mainPaper: { flex: 1, padding: theme.spacing(2), overflowY: "auto", minHeight: 0 },
  boardCard: {
    padding: theme.spacing(2), borderRadius: 10, cursor: "pointer",
    border: `1px solid ${theme.palette.divider}`, height: "100%",
    display: "flex", flexDirection: "column", gap: 8,
    transition: "box-shadow .15s", "&:hover": { boxShadow: theme.shadows[4] }
  },
  empty: { padding: theme.spacing(6), textAlign: "center", color: theme.palette.text.secondary },
  loadingWrap: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }
}));

const typeColor = (t) => (t === "pipeline" ? "#1565c0" : "#6a1b9a");

// Tile estilo Trello: o FUNDO do quadro (wallpaper/gradiente/cor) vira a capa do tile,
// com o nome sobreposto. Pra fundo "custom" busca a imagem autenticada (blob → object URL).
const BoardTile = ({ b, showArchived, onOpen, onArchive, onRestore, onDelete }) => {
  const [bgUrl, setBgUrl] = useState("");
  useEffect(() => {
    let active = true;
    if (b.background === "custom") {
      (async () => {
        try {
          const resp = await api.get(`/boards/${b.id}/background`, { responseType: "blob" });
          const url = window.URL.createObjectURL(resp.data);
          if (active) setBgUrl(url);
        } catch { if (active) setBgUrl(""); }
      })();
    } else {
      setBgUrl("");
    }
    return () => { active = false; };
  }, [b.id, b.background]);

  const bgStyle = b.background === "custom"
    ? (bgUrl
        ? { backgroundImage: `url("${bgUrl}")`, backgroundSize: "cover", backgroundPosition: "center" }
        : { background: "#37474f" })
    : (b.background ? boardBgStyle(b.background) : { background: typeColor(b.type) });

  return (
    <div onClick={onOpen}
      style={{ position: "relative", height: 132, borderRadius: 10, overflow: "hidden", cursor: "pointer",
        boxShadow: "0 1px 4px rgba(0,0,0,0.25)", ...bgStyle }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
        background: "linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0) 38%, rgba(0,0,0,0.62) 100%)" }} />
      <div style={{ position: "absolute", top: 4, right: 4, display: "flex" }} onClick={(e) => e.stopPropagation()}>
        {showArchived ? (
          <Tooltip title="Restaurar"><IconButton size="small" onClick={(e) => onRestore(b, e)} style={{ color: "#fff" }}><UnarchiveIcon fontSize="small" /></IconButton></Tooltip>
        ) : (
          <Tooltip title="Arquivar"><IconButton size="small" onClick={(e) => onArchive(b, e)} style={{ color: "#fff" }}><ArchiveIcon fontSize="small" /></IconButton></Tooltip>
        )}
        <Tooltip title="Excluir (definitivo)"><IconButton size="small" onClick={(e) => onDelete(b, e)} style={{ color: "#ff8a80" }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
      </div>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "10px 12px" }}>
        <Typography style={{ color: "#fff", fontWeight: 700, fontSize: 15, lineHeight: 1.2, textShadow: "0 1px 3px rgba(0,0,0,0.75)" }}>{b.name}</Typography>
      </div>
    </div>
  );
};

const Quadros = () => {
  const classes = useStyles();
  const history = useHistory();
  const { boardId } = useParams();

  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const fetchBoards = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listBoards(showArchived);
      setBoards(Array.isArray(data?.boards) ? data.boards : []);
    } catch (e) {
      toast.error("Falha ao carregar os quadros.");
      setBoards([]);
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => {
    if (!boardId) fetchBoards();
  }, [boardId, fetchBoards]);

  const handleArchive = async (b, e) => {
    e.stopPropagation();
    if (!window.confirm(`Arquivar o quadro "${b.name}"? (some da lista, sem apagar os cards)`)) return;
    try {
      await archiveBoard(b.id);
      fetchBoards();
    } catch (err) {
      toast.error(boardErrorMessage(err));
    }
  };
  const handleRestore = async (b, e) => {
    e.stopPropagation();
    try { await restoreBoard(b.id); fetchBoards(); } catch (err) { toast.error(boardErrorMessage(err)); }
  };
  const handleDelete = async (b, e) => {
    e.stopPropagation();
    if (!window.confirm(`EXCLUIR o quadro "${b.name}" e TODOS os cards/anexos? Esta ação é IRREVERSÍVEL.`)) return;
    try { await removeBoard(b.id); toast.success("Quadro excluído."); fetchBoards(); }
    catch (err) { toast.error(boardErrorMessage(err)); }
  };

  if (boardId) {
    return <BoardView boardId={boardId} onBack={() => history.push("/quadros")} />;
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Quadros</Title>
        <MainHeaderButtonsWrapper>
          <Button onClick={() => setShowArchived((v) => !v)} startIcon={showArchived ? <UnarchiveIcon /> : <ArchiveIcon />}>
            {showArchived ? "Ver ativos" : "Ver arquivados"}
          </Button>
          <NotificationsBell />
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setFormOpen(true)}>
            Novo Quadro
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        {loading ? (
          <div className={classes.loadingWrap}><CircularProgress /></div>
        ) : boards.length === 0 ? (
          <div className={classes.empty}>
            <Typography>{showArchived ? "Nenhum quadro arquivado." : 'Nenhum quadro ainda. Clique em "Novo Quadro" pra começar.'}</Typography>
          </div>
        ) : (
          <Grid container spacing={2}>
            {boards.map((b) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={b.id}>
                <BoardTile
                  b={b}
                  showArchived={showArchived}
                  onOpen={() => history.push(`/quadros/${b.id}`)}
                  onArchive={handleArchive}
                  onRestore={handleRestore}
                  onDelete={handleDelete}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
      <BoardFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={(created) => {
          setFormOpen(false);
          if (created && created.id) history.push(`/quadros/${created.id}`);
          else fetchBoards();
        }}
      />
    </MainContainer>
  );
};

export default Quadros;
